import { vnTemplate } from "@valnet/vnKnockoutJsTemplates/vnTemplate";
import { filterDate_template } from "~/components/templates/filterDate_template";
import { multipleSelector_template } from "@valnet/vnKnockoutJsTemplates/multipleSelector_template";
import { presetFilter_template } from "@valnet/vnKnockoutJsTemplates/presetFilter_template";
import { renderAuctionInsightsGrid } from "~/components/auction-insights/insights-grid";
import { html, LpButton, LpLoadingOverlay, LpPageHeader, raw } from "~/design-system";
import { createCopyLinkAction } from "~/utils/clipboard";
import { downloadCsv } from "~/utils/csvExport";
import { legacyIconNameToPhosphorIconName, setOptionalIconName } from "~/utils/iconName";
const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, {
    numeric: "auto",
});
function parseLastRefreshedAt(value) {
    if (!value) {
        return null;
    }
    const normalizedValue = value.includes("T") ? value : value.replace(" ", "T");
    const date = new Date(normalizedValue);
    return Number.isNaN(date.getTime()) ? null : date;
}
function formatLastRefreshedDateTime(date) {
    return date.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
    });
}
export class auctionInsights_template extends vnTemplate {
    getTemplateHtml() {
        return /* html */ `
            ${LpPageHeader({
            title: "Auction Insights",
            description: "Review auction win rates, carrier performance, and buyer pricing across click listings and data leads.",
            icon: "gavel",
            actions: LpButton({
                label: html `<span data-bind="text: copyLinkLabel"></span>`,
                prefixIcon: "link",
                size: "base",
                variant: "secondary",
                attributes: {
                    "data-bind": "click: onCopyLink",
                },
            }),
            controls: html `
                    <div class="insights-header-controls auction-insights-header-controls" data-bind="css: { 'is-open': showMoreFilters }">
                        <div
                            class="insights-filters-backdrop"
                            data-bind="click: closeMoreFilters, visible: showMoreFilters"
                        ></div>
                        <div class="insights-header-primary">
                            <div class="hidden">
                                ${raw(this.presetFilter.getHtmlBuild())}
                            </div>
                            <div class="auction-insights-filter-cell">
                                <div class="lib-date-picker">
                                    ${raw(this.datesFilter.getHtmlBuild())}
                                </div>
                            </div>
                            <span
                                class="lp-badge insights-refresh-badge"
                                data-variant="primary"
                                data-bind="visible: lastRefreshedLabel, text: lastRefreshedLabel"
                            ></span>
                        </div>
                        <div class="insights-filters-secondary insights-global-filters">
                            <header class="insights-global-filters__header">
                                <h2 class="insights-global-filters__title">Advanced Filters</h2>
                                ${LpButton({
                ariaLabel: "Close filters",
                prefixIcon: "x",
                size: "sm",
                shape: "square",
                variant: "ghost",
                className: "insights-global-filters__icon-btn",
                attributes: {
                    "data-bind": "click: closeMoreFilters",
                },
            })}
                            </header>
                            <div class="insights-global-filters__list insights-advanced-filter-groups">
                                ${raw(this.getDisplayFiltersHtml())}
                            </div>
                        </div>
                        <div class="insights-header-actions">
                            ${raw(this.getHeaderActionsHtml())}
                        </div>
                    </div>
                `,
        })}

			<div class="w-filters-selected">
				<div id="filters-selected" class="filters-selected">
					${raw(this.getSelectedFilterContainersHtml())}
				</div>
			</div>

			<div class="insights-results-shell" data-bind="css: { 'is-loading': requestState() === 'loading' }">
				${LpLoadingOverlay({
            label: "Loading auction insights...",
            loaderLabel: "Loading auction insights",
            className: "insights-loading-state",
            attributes: {
                "data-bind": "visible: requestState() === 'loading'",
            },
        })}
				<div id="list-results">
					${renderAuctionInsightsGrid()}
				</div>
			</div>

		`;
    }
    getDisplayFiltersHtml() {
        return [
            {
                title: "General",
                filters: [
                    this.brandFilter.getHtmlBuild(),
                    this.productFilter.getHtmlBuild(),
                    this.partnerFilter.getHtmlBuild(),
                    this.buyerFilter.getHtmlBuild(),
                    this.audienceFilter.getHtmlBuild(),
                ],
            },
            {
                title: "Auction Experiments",
                filters: [
                    this.clickAuctionExperimentFilter.getHtmlBuild(),
                    this.leadAuctionExperimentFilter.getHtmlBuild(),
                ],
            },
        ].map(({ title, filters }) => (this.getAdvancedFilterGroup(title, filters))).join("");
    }
    getHeaderActionsHtml() {
        return String(html `
            ${LpButton({
            label: "Filters",
            prefixIcon: "funnel",
            size: "base",
            variant: "ghost",
            attributes: {
                "data-bind": "click: toggleMoreFilters, attr: { 'aria-expanded': showMoreFilters() ? 'true' : 'false' }",
            },
        })}
            ${LpButton({
            label: "Reset",
            prefixIcon: "arrows-clockwise",
            variant: "primary",
            attributes: {
                "data-bind": "click: resetFilters",
            },
        })}
        `);
    }
    getSelectedFilterContainersHtml() {
        return [
            "brands",
            "products",
            "partners",
            "buyers",
            "audiences",
            "click-auction-experiments",
            "lead-auction-experiments",
        ].map((id) => `<div id="auction-insights-selected-${id}"></div>`).join("");
    }
    getAdvancedFilterGroup(title, filters) {
        return /* html */ `
            <section class="insights-advanced-filter-group">
                <h3 class="insights-advanced-filter-group__title">${title}</h3>
                <div class="insights-advanced-filter-group__list">
                    ${filters.map((bodyHtml) => this.getAdvancedFilterControl(bodyHtml)).join("")}
                </div>
            </section>
        `;
    }
    getAdvancedFilterControl(bodyHtml) {
        return /* html */ `
            <div class="insights-advanced-filter">
                ${bodyHtml}
            </div>
        `;
    }
    initPartial() {
        const vm = this.getTemplateViewModel();
        this.presetFilter = new presetFilter_template("presetFilter", vm, this.caller);
        this.presetFilter.setType("auction_insights");
        this.brandFilter = new multipleSelector_template("brandFilter", vm, this.caller);
        this.brandFilter.setLabel("brands");
        setOptionalIconName(this.brandFilter, legacyIconNameToPhosphorIconName("brand"));
        this.brandFilter.setFilterContainerClass("lib-dropdown");
        this.brandFilter.setTagContainerId("auction-insights-selected-brands");
        this.brandFilter.setTagContainerCaption("Selected brands:");
        this.productFilter = new multipleSelector_template("productFilter", vm, this.caller);
        this.productFilter.setLabel("products");
        setOptionalIconName(this.productFilter, legacyIconNameToPhosphorIconName("product"));
        this.productFilter.setFilterContainerClass("lib-dropdown");
        this.productFilter.setTagContainerId("auction-insights-selected-products");
        this.productFilter.setTagContainerCaption("Selected products:");
        this.partnerFilter = new multipleSelector_template("partnerFilter", vm, this.caller);
        this.partnerFilter.setLabel("partners");
        setOptionalIconName(this.partnerFilter, legacyIconNameToPhosphorIconName("users"));
        this.partnerFilter.setFilterContainerClass("lib-dropdown");
        this.partnerFilter.setTagContainerId("auction-insights-selected-partners");
        this.partnerFilter.setTagContainerCaption("Selected partners:");
        this.buyerFilter = new multipleSelector_template("buyerFilter", vm, this.caller);
        this.buyerFilter.setLabel("lead buyer");
        setOptionalIconName(this.buyerFilter, legacyIconNameToPhosphorIconName("users"));
        this.buyerFilter.setFilterContainerClass("lib-dropdown");
        this.buyerFilter.setTagContainerId("auction-insights-selected-buyers");
        this.buyerFilter.setTagContainerCaption("Selected buyers:");
        this.audienceFilter = new multipleSelector_template("audienceFilter", vm, this.caller);
        this.audienceFilter.setLabel("audience");
        setOptionalIconName(this.audienceFilter, legacyIconNameToPhosphorIconName("users"));
        this.audienceFilter.setFilterContainerClass("lib-dropdown");
        this.audienceFilter.setTagContainerId("auction-insights-selected-audiences");
        this.audienceFilter.setTagContainerCaption("Selected audiences:");
        this.clickAuctionExperimentFilter = new multipleSelector_template("clickAuctionExperimentFilter", vm, this.caller);
        this.clickAuctionExperimentFilter.setLabel("click auction experiment");
        setOptionalIconName(this.clickAuctionExperimentFilter, legacyIconNameToPhosphorIconName("bar-graph"));
        this.clickAuctionExperimentFilter.setFilterContainerClass("lib-dropdown");
        this.clickAuctionExperimentFilter.setTagContainerId("auction-insights-selected-click-auction-experiments");
        this.clickAuctionExperimentFilter.setTagContainerCaption("Selected click auction experiments:");
        this.leadAuctionExperimentFilter = new multipleSelector_template("leadAuctionExperimentFilter", vm, this.caller);
        this.leadAuctionExperimentFilter.setLabel("lead auction experiment");
        setOptionalIconName(this.leadAuctionExperimentFilter, legacyIconNameToPhosphorIconName("bar-graph"));
        this.leadAuctionExperimentFilter.setFilterContainerClass("lib-dropdown");
        this.leadAuctionExperimentFilter.setTagContainerId("auction-insights-selected-lead-auction-experiments");
        this.leadAuctionExperimentFilter.setTagContainerCaption("Selected lead auction experiments:");
        this.datesFilter = new filterDate_template("datesFilter", this.getTemplateViewModel(), this.caller);
        this.datesFilter.setStartPickerId("auction-insights-start-date-picker");
        this.datesFilter.setEndPickerId("auction-insights-end-date-picker");
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        let firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        if (date.getTime() === firstOfMonth.getTime()) {
            firstOfMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
            date.setDate(date.getDate() - 1);
        }
        else {
            date.setDate(date.getDate() - 1);
        }
        this.datesFilter.setDateRange(firstOfMonth, date);
    }
    getTemplateName() {
        return "auctionInsights_template";
    }
    getMainElementClasses() {
        return ["w-content", "auction-insights-page"];
    }
    buildViewModel() {
        const self = this;
        const { copyLinkLabel, onCopyLink } = createCopyLinkAction(ko);
        return {
            copyLinkLabel,
            onCopyLink,
            requestState: ko.observable("loading"),
            requestError: ko.observable(""),
            hasLoadedData: ko.observable(false),
            isLoading: ko.observable(0),
            lastRefreshedLabel: ko.observable(""),
            cpcAggregatorWinRate: ko.observableArray([]),
            cpcCarrierWinRate: ko.observableArray([]),
            cpcCarrierCtrs: ko.observableArray([]),
            cpcListingDistribution: ko.observableArray([]),
            cpcBuyers: ko.observableArray([]),
            leadAggregatorWinRate: ko.observableArray([]),
            leadCarrierWinRate: ko.observableArray([]),
            leadAggregatorFillRate: ko.observableArray([]),
            leadCarrierFillRate: ko.observableArray([]),
            leadBuyers: ko.observableArray([]),
            showMoreFilters: ko.observable(false),
            resetFilters: function (data) {
                self.resetFilters();
            },
            resetDisplayFilters: function () {
                self.resetDisplayFilters();
            },
            toggleMoreFilters: function () {
                const vm = self.getTemplateViewModel();
                const next = !vm.showMoreFilters();
                vm.showMoreFilters(next);
            },
            closeMoreFilters: function () {
                self.getTemplateViewModel().showMoreFilters(false);
            },
            onRetryData: function () {
                self.caller.getData();
            },
            formatNumeric: function (value, digits) {
                return self.formatNumeric(value, digits);
            },
            formatDelta: function (value, digits) {
                return self.formatDelta(value, digits);
            },
            formatMoney: function (value, digits) {
                return self.formatMoney(value, digits);
            },
            onExportTable: function (tableId) {
                self.exportTable(tableId);
            },
        };
    }
    getViewModelForRequest() {
        return {
            filters: {
                selectedBrands: this.brandFilter
                    .getSelectedItems()
                    .map((itemId) => ({ id: itemId })),
                selectedProducts: this.productFilter.getSelectedItems(),
                selectedPartners: this.partnerFilter.getSelectedItems(),
                selectedBuyers: this.buyerFilter.getSelectedItems(),
                selectedAudiences: this.audienceFilter.getSelectedItems(),
                selectedClickAuctionExperiments: this.clickAuctionExperimentFilter.getSelectedItems(),
                selectedLeadAuctionExperiments: this.leadAuctionExperimentFilter.getSelectedItems(),
                dateFilterType: this.datesFilter.getTemplateViewModel().selectedFilter(),
                startDate: this.datesFilter.getTemplateViewModel().startDate(),
                endDate: this.datesFilter.getTemplateViewModel().endDate(),
                startDateRange: this.datesFilter.getTemplateViewModel().fromRange(),
                endDateRange: this.datesFilter.getTemplateViewModel().toRange(),
            },
        };
    }
    activateLoading() {
        const viewModel = this.getTemplateViewModel();
        viewModel.requestError("");
        viewModel.requestState("loading");
    }
    completeLoading() {
        const viewModel = this.getTemplateViewModel();
        viewModel.hasLoadedData(true);
        viewModel.requestError("");
        viewModel.requestState("ready");
    }
    failLoading(message) {
        const viewModel = this.getTemplateViewModel();
        viewModel.requestError(message);
        viewModel.requestState("error");
    }
    buildDatePickerCalendar() {
        this.datesFilter.buildCalendarWhenReady();
    }
    destroyDatePickerCalendar() {
        this.datesFilter.destroyCalendars();
    }
    setBrandFilterItems(brands) {
        this.setMultiSelectItems(this.brandFilter, brands || []);
    }
    setProductFilterItems(items) {
        this.setMultiSelectItems(this.productFilter, items || []);
    }
    setPartnerFilterItems(items) {
        this.setMultiSelectItems(this.partnerFilter, items || []);
    }
    setBuyerFilterItems(items) {
        this.setMultiSelectItems(this.buyerFilter, items || []);
    }
    setAudienceFilterItems(items) {
        this.setMultiSelectItems(this.audienceFilter, items || []);
    }
    setClickAuctionExperimentFilterItems(items) {
        this.setMultiSelectItems(this.clickAuctionExperimentFilter, items || []);
    }
    setLeadAuctionExperimentFilterItems(items) {
        this.setMultiSelectItems(this.leadAuctionExperimentFilter, items || []);
    }
    setCpcAggregatorWinRate(data) {
        this.getTemplateViewModel().cpcAggregatorWinRate(data);
    }
    setCpcCarrierWinRate(data) {
        this.getTemplateViewModel().cpcCarrierWinRate(data);
    }
    setCpcCarrierCtrs(data) {
        this.getTemplateViewModel().cpcCarrierCtrs(data);
    }
    setCpcListingDistribution(data) {
        const row = data[0];
        if (!row) {
            this.getTemplateViewModel().cpcListingDistribution([]);
            return;
        }
        this.getTemplateViewModel().cpcListingDistribution([
            { listingsShown: 1, distribution: row.dist1Pct, overallCtr: row.ctr1Pct },
            { listingsShown: 2, distribution: row.dist2Pct, overallCtr: row.ctr2Pct },
            { listingsShown: 3, distribution: row.dist3Pct, overallCtr: row.ctr3Pct },
        ]);
    }
    setCpcBuyers(data) {
        this.getTemplateViewModel().cpcBuyers(data);
    }
    setLeadAggregatorWinRate(data) {
        this.getTemplateViewModel().leadAggregatorWinRate(data);
    }
    setLeadCarrierWinRate(data) {
        this.getTemplateViewModel().leadCarrierWinRate(data);
    }
    setLeadAggregatorFillRate(data) {
        this.getTemplateViewModel().leadAggregatorFillRate(data);
    }
    setLeadCarrierFillRate(data) {
        this.getTemplateViewModel().leadCarrierFillRate(data);
    }
    setLeadBuyers(data) {
        this.getTemplateViewModel().leadBuyers(data);
    }
    setStatus(status) {
        const maxDateCreated = status?.maxDateCreated ?? null;
        const label = this.formatLastRefreshedLabel(maxDateCreated);
        this.getTemplateViewModel().lastRefreshedLabel(label);
    }
    formatLastRefreshedLabel(value) {
        const date = parseLastRefreshedAt(value);
        if (!date) {
            return "";
        }
        const elapsed = Math.max(0, Date.now() - date.getTime());
        const minute = 60_000;
        const hour = 60 * minute;
        const day = 24 * hour;
        const dateTime = formatLastRefreshedDateTime(date);
        if (elapsed < minute) {
            return `Last refreshed just now · ${dateTime}`;
        }
        if (elapsed < hour) {
            return `Last refreshed ${relativeTimeFormatter.format(-Math.max(1, Math.floor(elapsed / minute)), "minute")} · ${dateTime}`;
        }
        if (elapsed < day) {
            return `Last refreshed ${relativeTimeFormatter.format(-Math.max(1, Math.floor(elapsed / hour)), "hour")} · ${dateTime}`;
        }
        return `Last refreshed ${relativeTimeFormatter.format(-Math.max(1, Math.round(elapsed / day)), "day")} · ${dateTime}`;
    }
    setMultiSelectItems(filter, items) {
        filter.setItems(items);
        //filter.setSeletedItemIds([]);
    }
    setViewModelFromRequest(params) {
        let selectedBrands = params["selectedBrands"];
        if (typeof selectedBrands !== "undefined" && !Array.isArray(selectedBrands)) {
            selectedBrands = [selectedBrands];
        }
        if (Array.isArray(selectedBrands)) {
            let normalizedSelectedBrands = selectedBrands.map((item) => {
                if (typeof item === "object" &&
                    item !== null &&
                    typeof item["id"] !== "undefined") {
                    return item["id"];
                }
                return item;
            });
            this.presetFilter.setMultiSelectorPresetFilters(this.brandFilter, normalizedSelectedBrands);
        }
        this.presetFilter.setMultiSelectorPresetFilters(this.productFilter, params["selectedProducts"]);
        this.presetFilter.setMultiSelectorPresetFilters(this.partnerFilter, params["selectedPartners"]);
        this.presetFilter.setMultiSelectorPresetFilters(this.buyerFilter, params["selectedBuyers"]);
        this.presetFilter.setMultiSelectorPresetFilters(this.audienceFilter, params["selectedAudiences"]);
        this.presetFilter.setMultiSelectorPresetFilters(this.clickAuctionExperimentFilter, params["selectedClickAuctionExperiments"]);
        this.presetFilter.setMultiSelectorPresetFilters(this.leadAuctionExperimentFilter, params["selectedLeadAuctionExperiments"]);
        this.presetFilter.setDates(this.datesFilter, params["dateFilterType"], params["startDate"], params["endDate"], params["startDateRange"], params["endDateRange"]);
    }
    formatDelta(value, digits = 0) {
        if (value === null) {
            return "N/A";
        }
        let n = Number(value);
        if (isNaN(n)) {
            n = 0;
        }
        return ((n > 0 ? "+" : "") +
            n.toLocaleString("en-EG", {
                minimumFractionDigits: digits,
                maximumFractionDigits: digits,
            }) +
            "%");
    }
    formatNumeric(value, digits = 0) {
        let n = Number(value);
        if (isNaN(n)) {
            n = 0;
        }
        return n.toLocaleString("en-EG", {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        });
    }
    formatMoney(value, digits = 0) {
        return "$" + this.formatNumeric(value, digits);
    }
    exportTable(tableId) {
        const vm = this.getTemplateViewModel();
        const exporters = {
            cpcAggregatorWinRate: {
                filename: "auction-cpc-win-rate-aggregator",
                headers: ["Aggregator", "Win Rate", "Delta Period"],
                rows: (vm.cpcAggregatorWinRate() || []).map((row) => [
                    row.aggregator,
                    `${this.formatNumeric(row.winRate, 0)}%`,
                    this.formatDelta(row.deltaWinRate, 0),
                ]),
            },
            cpcCarrierWinRate: {
                filename: "auction-cpc-win-rate-carrier",
                headers: ["Carrier", "Win Rate", "Delta"],
                rows: (vm.cpcCarrierWinRate() || []).map((row) => [
                    row.carrier,
                    `${this.formatNumeric(row.winRate, 0)}%`,
                    this.formatDelta(row.deltaWinRate, 0),
                ]),
            },
            cpcCarrierCtrs: {
                filename: "auction-cpc-ctr-carrier",
                headers: ["Carrier", "CTR", "Clicks", "Impressions"],
                rows: (vm.cpcCarrierCtrs() || []).map((row) => [
                    row.carrier,
                    `${this.formatNumeric(row.ctr, 2)}%`,
                    this.formatNumeric(row.clicks),
                    this.formatNumeric(row.impressions),
                ]),
            },
            cpcCarrierCtrsComparison: {
                filename: "auction-cpc-ctr-comparison",
                headers: ["Carrier", "CTR 1 Listing", "CTR 2 Listings", "CTR 3 Listings"],
                rows: (vm.cpcCarrierCtrs() || []).map((row) => [
                    row.carrier,
                    `${this.formatNumeric(row.ctr1Listing, 2)}%`,
                    `${this.formatNumeric(row.ctr2Listing, 2)}%`,
                    `${this.formatNumeric(row.ctr3Listing, 2)}%`,
                ]),
            },
            cpcListingDistribution: {
                filename: "auction-cpc-listing-distribution",
                headers: ["Listings Shown", "Distribution", "Overall CTR"],
                rows: (vm.cpcListingDistribution() || []).map((row) => [
                    row.listingsShown,
                    `${this.formatNumeric(row.distribution, 0)}%`,
                    `${this.formatNumeric(row.overallCtr, 0)}%`,
                ]),
            },
            cpcBuyers: {
                filename: "auction-cpc-buyers",
                headers: ["Carrier", "Avg Bid", "Avg Price"],
                rows: (vm.cpcBuyers() || []).map((row) => [
                    row.carrier,
                    this.formatMoney(row.avgBid, 2),
                    this.formatMoney(row.avgPrice, 2),
                ]),
            },
            leadAggregatorWinRate: {
                filename: "auction-lead-win-rate-aggregator",
                headers: ["Aggregator", "Win Rate", "Delta"],
                rows: (vm.leadAggregatorWinRate() || []).map((row) => [
                    row.aggregator,
                    `${this.formatNumeric(row.winRate, 0)}%`,
                    this.formatDelta(row.deltaWinRate, 0),
                ]),
            },
            leadCarrierWinRate: {
                filename: "auction-lead-win-rate-carrier",
                headers: ["Carrier", "Win Rate", "Delta"],
                rows: (vm.leadCarrierWinRate() || []).map((row) => [
                    row.carrier,
                    `${this.formatNumeric(row.winRate, 0)}%`,
                    this.formatDelta(row.deltaWinRate, 0),
                ]),
            },
            leadAggregatorFillRate: {
                filename: "auction-lead-fill-rate-aggregator",
                headers: ["Aggregator", "Fill Rate", "Delta"],
                rows: (vm.leadAggregatorFillRate() || []).map((row) => [
                    row.aggregator,
                    `${this.formatNumeric(row.fillRate, 0)}%`,
                    this.formatDelta(row.deltaFillRate, 0),
                ]),
            },
            leadCarrierFillRate: {
                filename: "auction-lead-fill-rate-carrier",
                headers: ["Carrier", "Fill Rate", "Delta"],
                rows: (vm.leadCarrierFillRate() || []).map((row) => [
                    row.carrier,
                    `${this.formatNumeric(row.fillRate, 0)}%`,
                    this.formatDelta(row.deltaFillRate, 0),
                ]),
            },
            leadBuyers: {
                filename: "auction-lead-buyers",
                headers: ["Carrier", "Avg Bid", "Avg Price"],
                rows: (vm.leadBuyers() || []).map((row) => [
                    row.carrier,
                    this.formatMoney(row.avgBid, 2),
                    this.formatMoney(row.avgPrice, 2),
                ]),
            },
        };
        const exporter = exporters[tableId];
        if (!exporter) {
            return;
        }
        downloadCsv(exporter.filename, exporter.headers, exporter.rows);
    }
    clearMultiFilterTags(filter) {
        const tvm = filter.getTemplateViewModel();
        filter.setSeletedItemIds([]);
        tvm.selectedItems([]);
        tvm.selectedGroups([]);
        tvm.selectedItemsOrderBySelection([]);
        tvm.openedGroupsDrawers([]);
        const el = filter.getTagContainer();
        if (el) {
            el.innerHTML = "";
        }
    }
    resetFilters() {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        let firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        if (date.getTime() === firstOfMonth.getTime()) {
            firstOfMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
            date.setDate(date.getDate() - 1);
        }
        else {
            date.setDate(date.getDate() - 1);
        }
        this.datesFilter.setDateRange(firstOfMonth, date);
        this.resetDisplayFilters(false);
        this.caller.getData();
    }
    resetDisplayFilters(reload = true) {
        this.clearMultiFilterTags(this.brandFilter);
        this.clearMultiFilterTags(this.productFilter);
        this.clearMultiFilterTags(this.partnerFilter);
        this.clearMultiFilterTags(this.buyerFilter);
        this.clearMultiFilterTags(this.audienceFilter);
        this.clearMultiFilterTags(this.clickAuctionExperimentFilter);
        this.clearMultiFilterTags(this.leadAuctionExperimentFilter);
        if (reload) {
            this.caller.getData();
        }
    }
}
